import {
  Action,
  AnyAction,
  combineReducers,
  configureStore,
  Reducer,
  ThunkAction
} from '@reduxjs/toolkit'
import {getConfig} from '../services/config'
import appViewReducer from './slices/appViewSlice'
import bookingReducer from './slices/bookingSlice'
import configReducer from './slices/configSlice'
import guestInfoReducer from './slices/guestInfoSlice'
import paymentReducer from './slices/paymentSlice'
import searchParamsReducer from './slices/searchParamsSlice'
import searchSubmittedReducer from './slices/searchSubmittedSlice'
import sessionReducer from './slices/sessionSlice'
import shoppingCartReducer from './slices/shoppingCartSlice'
import shoppingCartValidationReducer from './slices/shoppingCartValidationSlice'
import toastNotificationReducer from './slices/toastNotificationSlice'
import userReducer from './slices/userSlice'
import {
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER
} from 'redux-persist'
import {adyenPaymentsApi} from '../services/adyenPaymentsApi'
import {ibeApi} from '../services/ibeApi'
import {createPersistReducer} from './persist'
import {actionHistoryMiddleware} from './middleware/actionHistoryMiddleware'
import {analyticsMiddleware} from './middleware/analyticsMiddleware'
import {urlSyncMiddleware} from './middleware/urlSyncMiddleware'

declare global {
  interface Window {
    getRootState: () => RootState
  }
}

const {reduxDevTools} = getConfig()

const combinedReducer = combineReducers({
  appView: appViewReducer,
  booking: bookingReducer,
  config: configReducer,
  guestInfo: guestInfoReducer,
  payment: paymentReducer,
  searchParams: searchParamsReducer,
  searchSubmitted: searchSubmittedReducer,
  session: sessionReducer,
  shoppingCart: shoppingCartReducer,
  shoppingCartValidation: shoppingCartValidationReducer,
  toastNotification: toastNotificationReducer,
  user: userReducer,
  [adyenPaymentsApi.reducerPath]: adyenPaymentsApi.reducer,
  [ibeApi.reducerPath]: ibeApi.reducer
})

let rootState: RootState

const rootReducer: Reducer = (state: RootState, action: AnyAction) => {
  rootState = combinedReducer(state, action)
  return rootState
}

// create redux persist reducer - persists state to local storage
const persistentReducer = createPersistReducer(rootReducer)

// create redux store
export const store = configureStore({
  reducer: persistentReducer,
  middleware: getDefaultMiddleware => {
    // ignore non-serializable actions
    const persistOptions = {
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      }
    }
    return getDefaultMiddleware(persistOptions).concat([
      ibeApi.middleware,
      adyenPaymentsApi.middleware,
      actionHistoryMiddleware,
      analyticsMiddleware,
      urlSyncMiddleware
    ])
  },
  devTools: reduxDevTools
})

// create redux persistor
export const persistor = persistStore(store)

/**
 * Gets the root state of the redux store
 * @returns the root state of the redux store
 */
export function getRootState() {
  return rootState
}

// export the root state getter function globally
window.getRootState = getRootState

export type AppDispatch = typeof store.dispatch
export type RootState = ReturnType<typeof store.getState>
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>
